import React, { createContext, useEffect, useRef, useState } from 'react';
//mui
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Skeleton,
  Snackbar,
  styled,
  Box,
  Typography,
} from '@mui/material';
import { StyledSnackbar } from '../../common/AppSnackbar';
//mui-icons
import { ArrowForward, Science as ScienceIcon } from '@mui/icons-material';
//canvas infrastructure
import Canvas from './Canvas';
import Sidebar from './Sidebar';
import DraftCanvasHeader from '../../ProjectCanvas/Canvas/DraftCanvasHeader';
//component pane
import ComponentPane from '../ComponentPane/ComponentPane';
//dialogs
import FixErrorsDialog from '../../ProjectCanvas/WorkflowValidation/FixErrorsDialog';
import { CustomDialog } from '../../Component/Dialog';
//utils
import { getCurrentTime } from '../../../utils/getCurrentTime';
import {
  send_request,
} from '../../../utils/Request';
import { runLoading } from '../../../utils/showLoadingScreen';
//using redirects and router params
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
// Zustand
import useCanvasStore from './useCanvasStore';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import { evictAll as evictAllComponentStores } from '../../../utils/componentStoreRegistry';
import { prefillComponentStores } from '../../../utils/prefillComponentStores';
import { saveComponentByType } from '../../../utils/buildComponentSavePayload';
import { clearPlaceholderCache } from '../ComponentPane/ComponentPane';

import { computeAllPlaceholdersMap } from '../../../utils/placeholderExtractor';
import { isAnyForm } from '../../../utils/formTypeConstants';


//styling && css
import './Canvas.css';
import { loadDraftProject, loadPublishedProject, saveCanvasEvent, loadNextComponents } from '../../../utils/canvasApi';
import ClosePaneButton from '../ComponentPane/ClosePaneButton';
//saveEvent constructor
import { createSaveEvent } from '../../../utils/createSaveEvent';
import { fetchPlaceholderKeysAsMapV2 } from '../../../api/workflows';
import RenamePlaceholdersDialog from './RenamePlaceholderLogic/RenamePlaceholdersDialog';
import WorkflowPublishedDialog from './Dialogs/WorkflowPublishedDialog';
import { newLineToBreak } from '../../Task/TaskUtil';
import ErrorWorkflowDialog from '../../ProjectCanvas/Canvas/ErrorWorkflowDialog';
import TestPanel from '../../WorkflowTest/TestPanel';
import WorkflowAgentChat from './WorkflowAgentChat';
import { AI_AGENTS_CLIENT_IDS, isCanonicalPlaceholderMapEnabled } from '../../../constants/featureFlags';
import {
  normalizeLegacyPlaceholderMap,
  normalizeV2PlaceholderMap,
  comparePlaceholderMaps,
  emitPlaceholderParityTelemetry,
} from '../../../utils/placeholderParity';
import {
  emitPlaceholderMapSource,
  emitPlaceholderParityMismatch,
} from '../../../utils/rolloutTelemetry';
import dagre from 'dagre';
import { isNode } from 'reactflow';
import config from '../../../config';
import { getElements, extractNodes, extractEdges } from '../../../utils/CanvasUtil';
import { WorkflowPublishConfirmDialog } from './Dialogs/WorkflowPublishConfirmDialog';

const CanvasWrapper = styled('div')({
  flex: 1,
  position: 'relative',
  minHeight: 0,
});

const CanvasHeader = styled(Box)({
  flexShrink: 0,
  zIndex: 10,
  pointerEvents: 'auto',
});

export const CanvasContext = createContext([]);

const ActionButton = styled(Button, {
  shouldForwardProp: prop => !['bgColor', 'textColor', 'borderColor'].includes(prop),
})(({ bgColor, textColor, borderColor }) => ({
  backgroundColor: bgColor,
  color: textColor,
  border: '1px solid',
  borderColor: borderColor,
  '&:hover': {
    backgroundColor: bgColor,
    color: textColor,
    border: '1px solid',
    borderColor: borderColor,
  },
}));

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const WorkflowCanvas = props => {
    //react-router parameters to get workflow id out and use navigation
    const params = useParams();
    const navigate = useNavigate();
    const projectId = params.id;
    //workflow info
    const [workflow, setWorkflow] = useState({});
    const [lastModified, setLastModified] = useState(null);
    const [lastPublished, setLastPublished] = useState('');
    const [draftVersion, setDraftVersion] = useState(null);
    const [publishedVersion, setPublishedVersion] = useState('');
    const [parentFolderId, setParentFolderId] = useState(false);
    const [viewport, setViewport] = useState(null);

    //validation
    const [addressError, setAddressError] = useState(false);
    const [validation, setValidation] = useState(false);
    const [validatingType, setValidatingType] = useState('publish');
    //production mode
    const [production, setProduction] = useState(false);
    const [mode, setMode] = useState('DEFAULT');
    //ui utils
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingDialog, setLoadingDialog] = useState(false);
    //publish
    const [publishDialog, setPublishDialog] = useState(false);
    const [loadPublish, setLoadPublish] = useState(false);
    const [publishConfirmDialog, setPublishConfirmDialog] = useState(false);
    const [publishComment, setPublishComment] = useState('');
    //ai chat panel
    const [aiChatOpen, setAiChatOpen] = useState(false);
    const [numConfetti, setNumConfetti] = useState(0);

    // Zustand reactive reads
    const nodes = useCanvasStore(s => s.nodes);
    const edges = useCanvasStore(s => s.edges);
    const savedComponent = useProjectStore(s => s.savedComponent);
    const errorSaving = useProjectStore(s => s.errorSaving);
    const projectError = useProjectStore(s => s.error);
    const projectErrors = useProjectStore(s => s.errors);
    const projectComponents = useProjectStore(s => s.components);
    const projectCanvas = useProjectStore(s => s.canvas);
    const refreshCanvasValue = useProjectStore(s => s.refreshCanvas);
    const useBpmnJoinBehavior = useProjectStore(s => s.useBpmnJoinBehavior);
    const clientId = useProjectStore(s => s.clientId);
    const hasAiBuilderAccess = AI_AGENTS_CLIENT_IDS.includes(clientId);

    const [collapseNodes, setCollapseNodes] = useState(false);

    // Live-sync project store components from canvas state so placeholders
    // and downstream consumers update immediately when edges/nodes change.
    const prevEdgesRef = useRef(null);
    const prevNodesRef = useRef(null);
    useEffect(() => {
      const unsub = useCanvasStore.subscribe((state) => {
        const edgesChanged = state.edges !== prevEdgesRef.current;
        const nodesChanged = state.nodes !== prevNodesRef.current;
        if (!edgesChanged && !nodesChanged) return;
        prevEdgesRef.current = state.edges;
        prevNodesRef.current = state.nodes;
        useProjectStore.getState().syncFromCanvas(state.nodes, state.edges);
      });
      return unsub;
    }, []);

    const [isCanvasLoaded, setIsCanvasLoaded] = useState(false);
    const [notesInfoLoaded, setIsNotesInfoLoaded] = useState(false);
    const [isCanvasLocked, setIsCanvasLocked] = useState(false);
    //setting react flow instance to make sure we are saving the latest diagram
    const reactFlowWrapper = useRef(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    //styling
    const classes = props.classes;
    //Start
    const [showErrorSnackbar, setShowErrorSnackbar] = useState(null);

    // Listen for store errors from legacy components
    useEffect(() => {
      const handleStoreError = (e) => {
        setShowErrorSnackbar(e.detail?.message || 'An unexpected error occurred');
      };
      window.addEventListener('storeError', handleStoreError);
      return () => window.removeEventListener('storeError', handleStoreError);
    }, []);

    const [openStartDialog, setOpenStartDialog] = useState(false);
    const [renamePlaceholdersDialog, setRenamePlaceholdersDialog] = useState(false);
    const [openBrokenPHRename, setOpenBrokenPHRename] = useState(false);
    const [renameGivenPH, setRenameGivenPH] = useState(null);
    //access
    const [openAccess, setOpenAccess] = useState(false);
    //to handle unpublished changes
    const [changes, setChanges] = useState(false);
    //this flag is used to determine if we can issue any more events or we have to wait for the previous changes to finish saving
    const [saving, setSaving] = useState(false);
    // Additional query parameters
    const search = useLocation().search;
    const queryParams = new URLSearchParams(search);
    const publishQuery = queryParams.get('publish');
    const openTestPanelData = queryParams.get('openTestPanel');
    const isTrashView = queryParams.get('trash') === 'true';
    const openAiChatQuery = queryParams.get('openAiChat');

    //Editing components
    const [editComponent, setEditComponent] = useState(null);
    const [openComponentPane, setOpenComponentPane] = useState(false);
    const [pauseSave, setPauseSave] = useState(false); //stopping all save events on the canvas while component is open

    // warning about drawing links with component pane open
    const [linkWarning, setLinkWarning] = useState(false);
    // warning about double clicking into component when pane open
    const [navigationWarning, setNavigationWarning] = useState(false);
    const [validationLoading, setValidationLoading] = useState(false);
    const [showNotes, setShowNotes] = useState(true);
    const [hiddenNotes, setHiddenNotes] = useState([]);
    //we use this flag to ensure component is fully loaded in the pane before issuing a save
    const [childTemplateLoaded, setChildTemplateLoaded] = useState(false);
    //error workflwo dialog
    const [showErrorWorkflowDialog, setShowErrorWorkflowDialog] = useState(false);
    const [openTestPanel, setOpenTestPanel] = useState(false);
    const [openTestPanelAfterValidation, setOpenTestPanelAfterValidation] = useState(false);
    const [openTestTriggerDialog, setOpenTestTriggerDialog] = useState(false);
    const [showPublishDialogIfNoProductionVersion, setShowPublishDialogIfNoProductionVersion] =
      useState(false);

    const [notesContentArray, setNotesContentArray] = useState([]);
    const [notesLoaded, setNotesLoaded] = useState(false);
    const [testFromCompPanel, setTestFromCompPanel] = useState(false);
    const elementTypeArray = ['component', 'dbNode'];

    const [componentInfoForNodeHeader, setComponentInfoForNodeHeader] = useState([]);

    const [isInit, setIsInit] = useState(null);

    const [executeTest, setExecuteTest] = useState(false);

    const [fetchTestOutput, setFetchTestOutput] = useState(false);

    // prebuild preview diagram
    const [openPrebuildPreview, setOpenPrebuildPreview] = useState(false);
    const [mermaidJSContent, setMermaidJSContent] = useState('');

    // Trash view - workflow not found (needs restore)
    const [showRestorePrompt, setShowRestorePrompt] = useState(false);

    //Check if note is clicked, because if it is in this state, notes will save when deselected
    const [noteClicked, setNoteClicked] = useState(false);

    const [showComponentNotFoundDialog, setShowComponentNotFoundDialog] = useState(false);
    const [componentId, setComponentId] = useState(null);

    // Add state for pending connection
    const [pendingConnectionSource, setPendingConnectionSource] = useState(null);
    const lastNodeClickTime = useRef(0);

    const [allPlaceholdersInWorkflow, setAllPlaceholdersInWorkflowState] = useState({});
    const [isOutputPlaceholdersLoading, setIsOutputPlaceholdersLoading] = useState(false);

    // Global + credential placeholders – fetched once at canvas init for client-side computation
    const [globalPlaceholders, setGlobalPlaceholders] = useState([]);
    const [credentialPlaceholders, setCredentialPlaceholders] = useState([]);
    const [computedPlaceholdersInWorkflow, setComputedPlaceholdersInWorkflow] = useState({});
    const [canonicalPlaceholdersInWorkflow, setCanonicalPlaceholdersInWorkflow] = useState({});
    const [placeholderMapSource, setPlaceholderMapSource] = useState('frontend-computed');
    const [placeholderParityResult, setPlaceholderParityResult] = useState(null);
    const placeholderMapRequestRef = useRef(0);
    const canonicalFetchTimerRef = useRef(null);
    const hasFetchedCanonicalOnceRef = useRef(false);
    const hasCanonicalMapRef = useRef(false);

    const normalizePlaceholdersMap = raw => {
      if (!raw) return {};

      // Some environments may return a list instead of a map.
      if (Array.isArray(raw)) {
        const map = {};
        raw.forEach(item => {
          const componentId = item?.componentId || item?.id;
          const placeholders = item?.placeholders || item?.outputPlaceholders || item?.outputs;
          if (!componentId) return;
          map[String(componentId)] = Array.isArray(placeholders) ? placeholders : [];
        });
        return map;
      }

      if (typeof raw !== 'object') return {};

      const map = {};
      for (const [componentId, value] of Object.entries(raw)) {
        if (Array.isArray(value)) {
          map[componentId] = value;
          continue;
        }
        if (Array.isArray(value?.placeholders)) {
          map[componentId] = value.placeholders;
          continue;
        }
        // Unknown shape - keep it empty so hover UI doesn't throw.
        map[componentId] = [];
      }
      return map;
    };

    const setAllPlaceholdersInWorkflow = raw => {
      const normalized =
        raw?.schemaVersion === 'placeholder-map.v2'
          ? normalizeV2PlaceholderMap(raw)
          : normalizeLegacyPlaceholderMap(raw);
      setAllPlaceholdersInWorkflowState(normalized);
    };

    const toWorkflowVersionNumber = value => {
      if (value == null || value === '' || value === 'None') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const resolveWorkflowVersionForPlaceholderMap = explicitVersion => {
      const explicit = toWorkflowVersionNumber(explicitVersion);
      if (explicit != null) return explicit;

      const storeVersion = toWorkflowVersionNumber(useProjectStore.getState().version);
      if (storeVersion != null) return storeVersion;

      const draft = toWorkflowVersionNumber(draftVersion);
      if (draft != null) return draft;

      const productionVersionNum = toWorkflowVersionNumber(publishedVersion);
      if (productionVersionNum != null) return productionVersionNum;

      return null;
    };

    const refreshHybridPlaceholderMap = (componentsArg, explicitWorkflowVersion = null) => {
      const components = Array.isArray(componentsArg) ? componentsArg : [];

      const frontendMap = normalizeLegacyPlaceholderMap(computeAllPlaceholdersMap(components));
      setComputedPlaceholdersInWorkflow(frontendMap);

      // Only fall back to the frontend-computed map if we haven't loaded the
      // canonical (backend-v2) map yet.  When a canonical map already exists
      // (e.g. between auto-saves), keep it as the active placeholder source so
      // the UI doesn't flash broken placeholders while the refetch is in flight.
      if (!hasCanonicalMapRef.current) {
        setAllPlaceholdersInWorkflowState(frontendMap);
        setPlaceholderMapSource('frontend-computed');
      }

      if (!projectId || !isCanonicalPlaceholderMapEnabled(clientId)) {
        setCanonicalPlaceholdersInWorkflow({});
        hasCanonicalMapRef.current = false;
        setAllPlaceholdersInWorkflowState(frontendMap);
        setPlaceholderMapSource('frontend-computed');
        setPlaceholderParityResult(null);
        setIsOutputPlaceholdersLoading(false);
        return;
      }

      const workflowVersion = resolveWorkflowVersionForPlaceholderMap(explicitWorkflowVersion);
      const requestId = ++placeholderMapRequestRef.current;

      // Debounce the canonical fetch — rapid component updates (autosave, drag, etc.)
      // should not spam the v2 endpoint. First call (on load) fires immediately.
      if (canonicalFetchTimerRef.current) {
        clearTimeout(canonicalFetchTimerRef.current);
      }

      const doFetch = () => {
        if (requestId !== placeholderMapRequestRef.current) return;
        setIsOutputPlaceholdersLoading(true);

        fetchPlaceholderKeysAsMapV2(projectId, workflowVersion)
        .then(response => {
          if (requestId !== placeholderMapRequestRef.current) return;

          const canonicalMap = normalizeV2PlaceholderMap(response?.data);

          if (!Object.keys(canonicalMap).length) {
            setCanonicalPlaceholdersInWorkflow({});
            hasCanonicalMapRef.current = false;
            setPlaceholderParityResult(null);
            setAllPlaceholdersInWorkflowState(frontendMap);
            setPlaceholderMapSource('frontend-computed');
            return;
          }

          const comparison = comparePlaceholderMaps(canonicalMap, frontendMap);
          setPlaceholderParityResult(comparison);
          emitPlaceholderParityTelemetry({
            projectId,
            workflowVersion,
            comparison,
          });

          setCanonicalPlaceholdersInWorkflow(canonicalMap);
          hasCanonicalMapRef.current = true;
          setAllPlaceholdersInWorkflowState(canonicalMap);
          setPlaceholderMapSource('backend-v2');

          emitPlaceholderMapSource({ projectId, clientId, source: 'backend-v2' });
          if (!comparison.isMatch) {
            emitPlaceholderParityMismatch({ projectId, clientId, comparison });
          }
        })
        .catch(() => {
          if (requestId !== placeholderMapRequestRef.current) return;
          setCanonicalPlaceholdersInWorkflow({});
          hasCanonicalMapRef.current = false;
          setAllPlaceholdersInWorkflowState(frontendMap);
          setPlaceholderMapSource('frontend-computed');
          setPlaceholderParityResult(null);
          emitPlaceholderMapSource({ projectId, clientId, source: 'frontend-computed' });
        })
        .finally(() => {
          if (requestId === placeholderMapRequestRef.current) {
            setIsOutputPlaceholdersLoading(false);
          }
        });
      };

      // First canonical call fires immediately (initial load). Every later call is debounced.
      const runFetch = () => {
        canonicalFetchTimerRef.current = null;
        doFetch();
      };
      if (!hasFetchedCanonicalOnceRef.current) {
        hasFetchedCanonicalOnceRef.current = true;
        runFetch();
      } else {
        canonicalFetchTimerRef.current = setTimeout(runFetch, 800);
      }
    };

    const doesComponentExist = () => {
      let openedComponent = projectCanvas?.find(comp => comp.id === componentId);

      return !!openedComponent;
    };

    useEffect(() => {
      let paramComponentId = queryParams.get('view');
      if (!paramComponentId) return;
      setComponentId(paramComponentId);

      if (!doesComponentExist()) {
        setShowComponentNotFoundDialog(true);
        return;
      }

      setShowComponentNotFoundDialog(false);
    }, [projectCanvas]);

    //get workflow by id
    useEffect(() => {
      const intialise = async () => {
        setLoading(true);
        useProjectStore.setState({ production: false, canvasView: false });

        let notesToggle = false;

        if (useProjectStore.getState().production) {
          setProduction(true);
          setMode('PRODUCTION');
        }
        // Fetch global variables + credentials once (non-blocking) for client-side placeholder computation.
        Promise.all([
          send_request(`component-service/placeholder/global/${projectId}`, '', {}, 'get'),
          send_request(`component-service/placeholder/secret/${projectId}`, '', {}, 'get'),
        ]).then(([globalsRes, secretsRes]) => {
          setGlobalPlaceholders(globalsRes?.data?.placeholders || []);
          setCredentialPlaceholders(secretsRes?.data?.placeholders || []);
        }).catch(() => { /* best-effort */ });

        await getLatestVersion(projectId).then(async response => {
          const isProd = useProjectStore.getState().production;
          await (isProd
              ? loadPublishedProject(projectId)
              : loadDraftProject(projectId))
            .then(res => {
              let workflowData = res.data;

              if (!workflowData) {
                if (isTrashView) {
                  setShowRestorePrompt(true);
                  setLoading(false);
                }
                return Promise.reject(new Error('Workflow not found'));
              }


              //Load diagram into the store
              useProjectStore.getState().setWorkflow(workflowData);

              // Evict any stores that were created between cleanup and now
              // (e.g. by production canvas components still rendering during unmount)
              evictAllComponentStores();

              // Prefill keyed component stores from rawComponentData
              prefillComponentStores(workflowData.components, workflowData.version);

              // Compute output placeholder map client-side, then async-fetch canonical v2 map
              refreshHybridPlaceholderMap(workflowData.components, workflowData.version);

              // Show error dialog if status is ERROR
              if (workflowData && workflowData.status === 'ERROR') setShowErrorWorkflowDialog(true);

              //set diagram into the state
              setWorkflow(workflowData);
              document.title = `${workflowData.name ? workflowData.name : 'Untitled'} | Workflow86`;

              //set parent folder
              setParentFolderId(workflowData.parentFolderId);

              //set last dates
              setLastPublished(workflowData.lastPublished);
              setLastModified(workflowData.lastModified);

              //set viewport
              setViewport(workflowData.viewport);
              notesToggle = workflowData.showNotesToggle;
            })
            .then(() => {
              const ps = useProjectStore.getState();
              useProjectStore.setState({ canvas: ps.readDiagram(ps.components) });
            })
            .then(() => {
              const ps = useProjectStore.getState();
              let eles = ps.canvas.map(c => {
                if (elementTypeArray.includes(c.type) && c.data) c.data['componentId'] = c.id;
                return c;
              });
              useCanvasStore.getState().setNodesAndEdges(eles);

              //render the diagram
              getNotes(notesToggle);
              setLoading(false);
              setValidation(false);
            })
            .then(() => {
              if (publishQuery === 'true') {
                setPublishDialog(true);
              }
              if (openTestPanelData !== null) {
                setOpenTestPanel(openTestPanelData);
              }
              if (openAiChatQuery === 'true' && hasAiBuilderAccess) {
                setAiChatOpen(true);
              }
            })
            .catch(e => {
              console.log(e);
              setLoading(false);
              // If in trash view and project not found, show appropriate message
              if (isTrashView) {
                setIsCanvasLocked('This workflow is in Trash. Restore it to make changes.');
              }
            });
        });
      };
      intialise();
      
      // If in trash view, lock the canvas
      if (isTrashView) {
        setIsCanvasLocked('Viewing deleted workflow (read-only)');
      }

      return () => {
        placeholderMapRequestRef.current += 1;
        if (canonicalFetchTimerRef.current) clearTimeout(canonicalFetchTimerRef.current);
        hasFetchedCanonicalOnceRef.current = false;
        hasCanonicalMapRef.current = false;
        evictAllComponentStores();
      };
    }, []);

    /** Recompute output placeholder map from current store data + async canonical fetch. */
    const refreshAllPlaceholdersInWorkflow = () => {
      const components = useProjectStore.getState().components;
      refreshHybridPlaceholderMap(components);
    };

    // Reactively recompute the output placeholder map whenever the components
    // array reference changes (e.g. after a form save updates rawComponentData).
    // This replaces the need for explicit refreshAllPlaceholdersInWorkflow() calls.
    useEffect(() => {
      const unsub = useProjectStore.subscribe(
        (s) => s.components,
        (components) => {
          if (components && components.length > 0) {
            refreshHybridPlaceholderMap(components);
          }
        },
      );
      return unsub;
    }, []);

    // Method for re-fetching the canvas components
    const refetchComponents = async () => {
      const res = await loadDraftProject(projectId);
      if (!res || !res.data) return;

      let workflowData = res.data;
      const cs = useCanvasStore.getState();
      const notesAndLinks = [
        ...cs.nodes.filter(item => item.type === 'note'),
        ...cs.edges.filter(item => item.label !== 'componentLink'),
      ];

      const ps = useProjectStore.getState();
      useProjectStore.setState({ components: workflowData.components });
      const canvas = ps.readDiagram(workflowData.components);
      useProjectStore.setState({ canvas });
      let eles = canvas.map(c => {
        if (elementTypeArray.includes(c.type) && c.data) c.data['componentId'] = c.id;
        return c;
      });

      const refetchedNodesAndEdges = [...eles, ...notesAndLinks];
      useCanvasStore.getState().setNodesAndEdges(refetchedNodesAndEdges);
      return refetchedNodesAndEdges;
    };

    useEffect(() => {
      const loadAndSaveNotes = async () => {
        if (reactFlowInstance) {
          if (!showNotes) {
            //getting latest position
            let notesAndLinks = getElements(reactFlowInstance).filter(
              x =>
                x.type === 'note' ||
                (x.type === 'link' && x.sourceHandle && x.sourceHandle.startsWith('note'))
            );
            let newElements = getElements(reactFlowInstance).filter(
              x =>
                elementTypeArray.includes(x.type) ||
                (x.data && x.data.hasDb) ||
                (x.type === 'link' && x.label === 'componentLink')
            );

            useCanvasStore.getState().setNodesAndEdges(newElements);

            setHiddenNotes(notesAndLinks);
          } else {
            let notes = hiddenNotes ? hiddenNotes : [];

            useCanvasStore.getState().setNodesFn(ns => [...ns, ...extractNodes(notes)]);
            useCanvasStore.getState().setEdgesFn(es => [...es, ...extractEdges(notes)]);
          }
          let rfiObj = reactFlowInstance.toObject();

          let viewport = {
            x: rfiObj.viewport.x.toFixed(5),
            y: rfiObj.viewport.y.toFixed(5),
            zoom: rfiObj.viewport.zoom.toFixed(5),
          };

          let eventData = {
            showNotes: showNotes,
          };
          let saveEvent = createSaveEvent(
            'SAVE_NOTES_TOGGLE',
            projectId,
            viewport,
            getElements(reactFlowInstance),
            eventData
          );

          //send event
          await saveCanvasEvent(projectId, saveEvent)
            .then(response => {
              if (response.data.updated) {
                setLastModified(response.data.lastModified);
              }
              //insert delay here
              setSaving(false);
            })
            .catch(error => {});
        }
      };
      loadAndSaveNotes();
    }, [showNotes]);

    const updateNotesContent = (notes, newElements) => {
      let notesWithContent = [];

      // Go over each existing canvas note
      for (let idx = 0; idx < notes.length; idx++) {
        const note = notes[idx];

        let newNote = { ...note };

        let noteData = notesContentArray.filter(x => x.id === note.id);

        // If the note data exists, set the content
        if (noteData && noteData.length > 0 && newNote.data) {
          newNote.data.content = noteData[0].content;
        }

        notesWithContent.push(newNote);
      }

      return [...newElements, ...notesWithContent];
    };

    const applyLayout = (elements, parentPosition) => {
      const nodeWidth = 200;
      const nodeHeight = 50;
      const isHorizontal = true;
      dagreGraph.setGraph({ rankdir: 'LR' });

      elements.forEach(el => {
        if (isNode(el) && el.position.x === 0 && el.position.y === 0) {
          dagreGraph.setNode(el.id, { width: nodeWidth, height: nodeHeight });
        } else {
          dagreGraph.setEdge(el.source, el.target);
        }
      });

      dagre.layout(dagreGraph);

      return elements.map(el => {
        if (isNode(el) && el.position.x === 0 && el.position.y === 0) {
          const nodeWithPosition = dagreGraph.node(el.id);
          el.targetPosition = isHorizontal ? 'left' : 'top';
          el.sourcePosition = isHorizontal ? 'right' : 'bottom';

          // unfortunately we need this little hack to pass a slightly different position
          // to notify react flow about the change. Moreover we are shifting the dagre node position
          // (anchor=center center) to the top left so it matches the react flow node anchor point (top left).
          el.position = {
            x: nodeWithPosition.x + parentPosition.x + Math.random() / 1000,
            y: nodeWithPosition.y + parentPosition.y,
          };
        }

        return el;
      });
    };

    useEffect(() => {
      const loadNotes = async () => {
        if (reactFlowInstance && notesContentArray) {
          // Grab everything not a note
          const nonNotes = nodes.filter(node => node.type !== 'note');

          // Grab everything that is a note
          const existingNotes = nodes.filter(node => node.type === 'note');

          let newElements = updateNotesContent(existingNotes, nonNotes);

          /**
           * Update content in hidden notes in case the notes are hidden initially
           * @type {*[]}
           */
          let newHiddenNotes = [];
          updateNotesContent(hiddenNotes, newHiddenNotes);

          //update canvas
          useCanvasStore.getState().setNodes(extractNodes(newElements));
        }

        setNotesLoaded(true);
      };
      loadNotes();
    }, [notesContentArray]);

    //handle canvas validation, duplicate placeholders and individual placeholder validation
    const validate = async canvasComponents => {
      //clear error array before new validation
      useProjectStore.setState({ errors: [], error: false });

      //make request to project service to validate the canvas
      await send_request(`project-service/validate/${useProjectStore.getState().project_id}`, '', '', 'GET')
        .then(res => {
          if (res && res.data) {
            let d = res.data;
            let errMap = {};
            errMap = addToHashMap(errMap, d.placeholdersNotUnique, 'notUnique');
            errMap = addToHashMap(errMap, d.componentErrors, 'componentError');
            errMap = addToHashMap(errMap, d.nonExistantPlaceholders, 'broken');
            addCanvasErrors(errMap, canvasComponents);
          }

          // Merge canvas position/next data into existing components instead of
          // replacing them, so that rawComponentData and other fields are preserved.
          const existing = useProjectStore.getState().components;
          const existingMap = new Map(existing.map(c => [c.componentId, c]));
          const merged = canvasComponents.map(cc => {
            const full = existingMap.get(cc.componentId);
            if (full) {
              return {
                ...full,
                viewData: cc.viewData,
                nextComponents: cc.nextComponents || full.nextComponents,
                componentData: { ...full.componentData, name: cc.componentData?.name || full.componentData?.name },
              };
            }
            return cc;
          });
          useProjectStore.setState({ components: merged });

          const currentErrors = useProjectStore.getState().errors;
          if (currentErrors.length > 0) useProjectStore.setState({ error: true });
          else useProjectStore.setState({ error: false });
        })
        .finally(() => {})
        .catch(e => console.log(e));
    };

    const addToHashMap = (errMap, errs, errType) => {
      for (let error of errs) {
        let [first, ...rest] = error.split('_');
        rest = rest.join('_');

        let res = {
          errorMessage: rest,
          errorType: errType,
          isWarning: errType === 'notUnique',
        };

        if (first in errMap) errMap[first].push(res);
        else errMap[first] = [res];
      }
      return errMap;
    };

    const addCanvasErrors = (errorMap, components) => {
      let errItems = [];

      for (let i = 0; i < components.length; i++) {
        let c = components[i];

        if (!(c.componentId in errorMap)) continue;
        let errs = errorMap[c.componentId];

        for (let j = 0; j < errs.length; j++) {
          let errMsg = errs[j].errorMessage;

          let item = {};
          item.component = c;
          item.error = errMsg;
          item.type = errs[j].errorType;
          item.isWarning = errs[j].errorType === 'notUnique';
          errItems.push(item);
          // c.error = true;
        }
      }

      useProjectStore.setState({ errors: errItems });
    };

    const fetchWorkflowDescription = async () => {
      const currentDescription = workflow?.description || '';
      try {
        const response = await loadDraftProject(projectId);
        const fetchedDescription = response?.data?.description;

        //Covers the case when users intentionally wants to clear the project description
        if (fetchedDescription || fetchedDescription.trim() === '') {
          setWorkflow(prev => ({ ...prev, description: fetchedDescription }));
          return fetchedDescription;
        }

        return currentDescription;
      } catch (error) {
        console.error('Error fetching workflow description:', error);
        return currentDescription;
      }
    };

    const getNotesContent = async () => {
      const url =
        'project-service/project/get_notes_count?projectId=' + projectId + '&isLatestVersion=true';

      let notesWithContent = [];
      const noteCountRes = await send_request(url, null, null, 'GET');
      if (!noteCountRes || !noteCountRes.data) return;
      for (let pageNumber = 1; pageNumber <= noteCountRes.data.pageSize; pageNumber++) {
        const url =
          'project-service/project/get_project_notes_content?projectId=' +
          projectId +
          '&isLatestVersion=true&pageNumber=' +
          pageNumber;
        const noteContent = await send_request(url, null, null, 'GET');
        // If data, set into the notesWithContent array
        if (noteContent && noteContent.data)
          notesWithContent = [...notesWithContent, ...noteContent.data];

        // Check if we've reached the total note count
        // If so, set the notes content array
        if (notesWithContent.length >= noteCountRes.data.totalNoteCount) {
          setNotesContentArray(notesWithContent);
          return;
        }
      }

      // After going through all pages,
      // if not all notes are fetched, update the canvas with fetched notes
      if (notesContentArray.length === 0) setNotesContentArray(notesWithContent);
    };

    useEffect(() => {
      // If notes info is loaded, get and set the note content
      const retrieveNoteContent = async () => {
        if (notesInfoLoaded) getNotesContent();
      };
      retrieveNoteContent();
    }, [notesInfoLoaded]);

    //Retrieves notes
    const getNotes = notesToggle => {
      //load notes
      (useProjectStore.getState().production
        ? loadPublishedProject(projectId)
        : loadDraftProject(projectId))
        .then(res => {
          var notes = null;

          notes = res.data ? res.data.notes : [];
          notes = notes ? useProjectStore.getState().readNotes(notes) : [];

          useCanvasStore.getState().addNodes(extractNodes(notes));
          useCanvasStore.getState().addEdges(extractEdges(notes));

          setIsCanvasLoaded(true);
          setIsNotesInfoLoaded(true);
        })
        .then(() => {
          if (notesToggle != null && notesToggle != undefined) {
            setShowNotes(notesToggle);
          }

          // If viewing a pane, we need to open it
          let componentId = queryParams.get('view');
          if (!componentId) return;

          // Else we need to open the pane
          let openedComponent = useProjectStore.getState().canvas?.find(comp => comp.id === componentId);
          if (!openedComponent) return;

          setEditComponent({
            componentId: componentId,
            ...openedComponent.data,
          });
        })
        .catch(e => {
          console.log(e);
        });
    };

    useEffect(() => {
      useProjectStore.setState({ nodes, edges });
    }, [nodes, edges]);

    useEffect(() => {
      const openComponentPane = async () => {
        //catch updates to the component we are targeting to edit.
        if (editComponent != null) {
          //open sidebar
          setOpenComponentPane(true);
          //stop all save events for the canvas
          setPauseSave(true);
          if (openTestPanelAfterValidation !== null && openTestPanelAfterValidation !== false)
            setOpenTestPanelAfterValidation(null);
        }
      };
      openComponentPane();
    }, [editComponent]);

    const checkUnpublishedChanges = async (
      fromRun = false,
      forceStartId = null,
      publishDialog = false
    ) => {
      if (publishedVersion === 'None') {
        setLoadingDialog(false);
        setShowPublishDialogIfNoProductionVersion(true);
      } else {
        const url = `project-service/project/unpublished_changes/${projectId}`;
        return await send_request(url, '', '', 'GET')
          .then(res => {
            if (!res || res.status !== 200) return;

            setChanges(res.data);
            setLoadingDialog(false);

            if (publishDialog) return; // If publish dialog is true, return

            // Navigate directly to production version (skip unpublished changes dialog)
            if (forceStartId === null) navigate(`/project/production/${projectId}`);
            else navigate(`/project/production/${projectId}?force_start=${forceStartId}`);
          })
          .catch(e => console.log(e));
      }
    };

    const getLatestVersion = async projectId => {
      return send_request(`project-service/project/last-version/${projectId}`, '', '', 'get').then(
        res => {
          if (res && res.data) {
            let { draftVersion, publishedVersion } = res.data;

            if (!draftVersion) draftVersion = `${publishedVersion}`;
            else draftVersion = `${draftVersion}`;
            setDraftVersion(draftVersion);

            if (!publishedVersion) publishedVersion = 'None';
            else publishedVersion = `${publishedVersion}`;
            setPublishedVersion(publishedVersion);

            useProjectStore.getState().setLatestVersion(draftVersion, publishedVersion);
          }
        }
      );
    };

    //Saving
    const save = async (fromConditionalWorkflow = false) => {
      if (saving) return; // If saving, return

      setSaving(true);
      //thi function now only saves component position
      const shouldSave = (fromConditionalWorkflow || !pauseSave) && reactFlowInstance;
      if (shouldSave) {
        // handleCloseSnackbar(); //close snackbar
        //get the latest node position
        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let saveEvent = createSaveEvent(
          'SAVE_VIEWPORT',
          projectId,
          viewport,
          getElements(reactFlowInstance),
          { projectId: projectId }
        );

        //send event
        await saveCanvasEvent(projectId, saveEvent)
          .then(response => {
            if (response.data.updated) {
              setLastModified(response.data.lastModified);
            }
            //insert delay here
            setSaving(false);
          })
          .catch(error => {});
      }
    };

    const validateComponents = validatedCanvas => {
      nodes.map(element => {
        validatedCanvas.map(component => {
          if (
            component.type == 'component' &&
            element.type == 'component' &&
            component.id == element.id &&
            component.data.error
          ) {
            element.data.error = component.data.error;
          }
        });
      });
      useCanvasStore.getState().setNodes(nodes);
    };

    //Publishing
    const onPublish = async description => {
      setLoadPublish(true);

      // If description is provided and different from current, update workflow state
      if (description !== undefined && description !== workflow?.description) {
        setWorkflow(prev => ({ ...prev, description }));
      }

      publish(description).then(() => {
        setLoadPublish(false);
        fetchWorkflowDescription();
      });
    };

    const saveDraftVersion = async () => {
      let versionEstablished = {};

      let url = 'authz-service/user-info';
      const json = await send_request(url, null, null, 'GET');
      if (json && json.data) versionEstablished['by'] = json.data.displayName;

      url = `project-service/project/save-draft-version/${projectId}`;

      send_request(url, versionEstablished, '', 'POST')
        .then(async () => {
          getLatestVersion(projectId);
          setShowSnackbar(true);
        })
        .catch(e => {
          console.log(e);
        });
    };

    const publish = async description => {
      if (saving) return; // If saving, return

      setSaving(true);

      // Update description if provided
      if (description !== undefined) {
        const updateValue = {
          projectId: projectId,
          description: description,
          isLatestVersion: true,
        };
        await send_request('project-service/project/details', updateValue, '', 'patch').catch(e => {
          console.log('Failed to update description:', e);
        });
      }

      let rfiObj = reactFlowInstance.toObject();

      let viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      let versionEstablished = {
        comment: publishComment ? newLineToBreak(publishComment) : publishComment,
      };

      let url = 'authz-service/user-info';
      const json = await send_request(url, null, null, 'GET');
      if (json && json.data) versionEstablished['by'] = json.data.displayName;

      const elements = getElements(reactFlowInstance);

      let saveEvent = createSaveEvent(
        'PUBLISH',
        projectId,
        viewport,
        elements,
        {
          versionEstablished: versionEstablished,
        }
      );

      //send event
      await saveCanvasEvent(projectId, saveEvent)
        .then(async response => {
          console.log('[publish] saveCanvasEvent response:', response?.data);
          if (response.data.updated) {
            setLastModified(response.data.lastModified);
          }
          setSaving(false);
          setPublishDialog(false);
          setPublishConfirmDialog(true);
          setLastPublished(getCurrentTime());
          await getLatestVersion(projectId);
          console.log('[publish] about to reloadDraftCanvas');
          try { await reloadDraftCanvas(); } catch (_) { /* best-effort */ }
        })
        .catch(error => {
          console.error('[publish] ERROR:', error);

          setPublishDialog(false);
        });
    };

    const fireValidateDialog = async type => {
      setShowPublishDialogIfNoProductionVersion(false);
      setOpenTestPanelAfterValidation(null);
      setValidatingType(type);
      setValidationLoading(true);

      //save the diagram
      await save().then(async () => {
        // To fix validation not working on initial load
        let result = getElements(reactFlowInstance);
        let newComponents = useProjectStore.getState().readCanvasIntoComponents(result);

        setValidation(true);

        await validate(newComponents)
          .then(() => {
            return useProjectStore.getState().readDiagram(newComponents);
          })
          .then(validatedCanvas => {
            //revalidate components on the diagram
            validateComponents(validatedCanvas);
            setValidation(false);
          });
      });

      setValidationLoading(false);
      setSaving(false);

      if (useProjectStore.getState().error) setAddressError(true);
      else {
        if (type === 'publish') {
          setPublishDialog(true);
          setAddressError(false);
        }
        // else  setOpenTestPanelAfterValidation(openTestPanel)
      }
    };

    const reloadDraftCanvas = async () => {
      console.log('[reloadDraftCanvas] starting reload for', projectId);
      evictAllComponentStores();

      const res = await loadDraftProject(projectId);
      const workflowData = res.data;
      if (!workflowData) {
        console.error('[reloadDraftCanvas] loadDraftProject returned no data!');
        return;
      }

      const comps = workflowData.components || [];
      const nullVd = comps.filter(c => !c.viewData);
      console.log('[reloadDraftCanvas] loaded version:', workflowData.version,
        'components:', comps.length,
        'nullViewData:', nullVd.length);
      if (nullVd.length > 0) {
        console.error('[reloadDraftCanvas] SERVER RETURNED NULL VIEWDATA!',
          nullVd.map(c => ({ id: c.componentId, type: c.type })));
      }


      useProjectStore.getState().setWorkflow(workflowData);
      evictAllComponentStores();
      prefillComponentStores(workflowData.components, workflowData.version);
      refreshHybridPlaceholderMap(workflowData.components, workflowData.version);

      setWorkflow(workflowData);
      setLastPublished(workflowData.lastPublished);
      setLastModified(workflowData.lastModified);
      setViewport(workflowData.viewport);

      const ps = useProjectStore.getState();
      useProjectStore.setState({ canvas: ps.readDiagram(ps.components) });

      const ps2 = useProjectStore.getState();
      const eles = ps2.canvas.map(c => {
        if (elementTypeArray.includes(c.type) && c.data) c.data['componentId'] = c.id;
        return c;
      });
      useCanvasStore.getState().setNodesAndEdges(eles);

      const notes = workflowData.notes ? useProjectStore.getState().readNotes(workflowData.notes) : [];
      useCanvasStore.getState().addNodes(extractNodes(notes));
      useCanvasStore.getState().addEdges(extractEdges(notes));
    };

    const onDonePublish = async () => {
      await checkUnpublishedChanges(false, null, true);
      setNumConfetti(0);
      setPublishConfirmDialog(false);
      setPublishComment('');
    };




    const modifyProjectData = async () => {
      let updateValue = {};
      updateValue.projectId = projectId;
      updateValue.lastModified = getCurrentTime();
      updateValue.description = workflow.description;
      updateValue.categories = workflow.categories;
      updateValue.imagePath =
        workflow.imagePath != null ? workflow.imagePath : '/images/blue-blocks2.jpg';
      updateValue.isLatestVersion = true;
      await send_request('project-service/project/details', updateValue, '', 'patch').catch(e => {
        console.log(e);
      });
    };

    const handleConditionalWorflowPathsRefresh = async () => {
      const refreshCanvasId = useProjectStore.getState().refreshCanvas;
      const id = nodes.find(obj => obj.id === refreshCanvasId);
      const componentIdsToRemove = (id.data.next || []).map(obj => obj.componentId);

      let filteredObjects = nodes.filter(
        obj =>
          !componentIdsToRemove.includes(obj.id) &&
          obj.id !== refreshCanvasId &&
          !(obj.type === 'link' && obj.source === id.id)
      );

      loadNextComponents(projectId, refreshCanvasId)
        .then(res => {
          const diag = useProjectStore.getState().readDiagram(res.data);
          id.data.next = diag.map(obj => ({ componentId: obj.id }));

          const links = [];
          if (diag.length > 0) {
            diag.forEach(next => {
              next.data.parentId = id.id; //set parent id for new components
              const newLink = useProjectStore.getState().createLink(
                id.data.type,
                id.data.fromWorkflowPath,
                id.id,
                next.id,
                id.data.baseColor,
                'componentLink'
              );
              links.push(newLink);
            });
          }

          const ele = applyLayout([...diag, ...links], id.position);

          const finalList = [...filteredObjects, ...edges, ...ele, id];
          useCanvasStore.getState().setNodesAndEdges(finalList);
        })
        .finally(() => {
          useProjectStore.setState({ refreshCanvas: null });

          // moving it to finally to ensure the save doesn't trigger the refresh canvas
          // again and create dangling components
          save(true);
        });
    };

    //Ui stuff
    const handleCloseSnackbar = () => {
      setShowSnackbar(false);
      useProjectStore.setState({ savedComponent: false });
      clearPlaceholderCache();

      if (useProjectStore.getState().refreshCanvas) handleConditionalWorflowPathsRefresh();

      useProjectStore.setState({ errorSaving: false });
      setLinkWarning(false);
      setNavigationWarning(false);
    };

    const handleCloseTestPanel = () => {
      setOpenTestPanelAfterValidation(null);
      setOpenTestPanel(false);
      removeForceStartTestParams();
      if (window.location.href.includes('?force_start=')) navigate(`/project/canvas/${projectId}`);
    };

    const handleTestExpand = () => {
      let expandedWindow = window.open(`/test_workflow/${projectId}/${openTestPanel}`, '_blank');

      expandedWindow.version = draftVersion;
      expandedWindow.projectName = workflow.name;
      expandedWindow.lastModified = lastModified;
    };

    const handleBack = () => {
      useProjectStore.setState({ production: false });
      if (parentFolderId) {
        navigate('/project/folder/' + parentFolderId);
      } else {
        navigate('/project');
      }
    };

    const showSuccess = (data, saveTriggerData) => {
      //show snackbar on save
      if (saveTriggerData?.executeTest) setExecuteTest(true);

      // Recompute output placeholder map from current store data after save.
      refreshAllPlaceholdersInWorkflow();
    };

    const showCanvasLoadingSkeleton = () => {
      return (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Grid
            container
            spacing={1}
            marginTop="8px"
            paddingLeft={'16px'}
            paddingRight={'16px'}
            marginBottom={'24px'}
          >
            <Grid item>
              <Skeleton
                variant={'rectangular'}
                height={25}
                width={'30px'}
                style={{ borderRadius: '4px' }}
              />
            </Grid>
            <Grid item marginRight="16px">
              <Skeleton
                variant={'rectangular'}
                height={25}
                width={'130px'}
                style={{ borderRadius: '4px' }}
              />
            </Grid>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(item => {
              return (
                <Grid key={item} item>
                  <Skeleton
                    variant={'rectangular'}
                    height={25}
                    width={'90px'}
                    style={{ borderRadius: '4px' }}
                  />
                </Grid>
              );
            })}
            <Grid item marginLeft="16px">
              <Skeleton
                variant={'rectangular'}
                height={25}
                width={'130px'}
                style={{ borderRadius: '4px' }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            paddingLeft={'16px'}
            paddingRight={'16px'}
          >
            <Skeleton
              height={'85vh'}
              width={'100%'}
              variant={'rectangular'}
              style={{ borderRadius: '4px' }}
            />
          </Grid>
        </div>
      );
    };

    const handleSaveProjectAttributes = async data => {
      if (saving) return;

      setSaving(true);
      setWorkflow({
        ...workflow,
        name: data.name,
      });

      useProjectStore.getState().setProjectName(data.name);

      let rfiObj = reactFlowInstance.toObject();

      let viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      let saveEvent = createSaveEvent(
        'SAVE_WORKFLOW_NAME',
        projectId,
        viewport,
        getElements(reactFlowInstance),
        data
      );

      //send event
      await saveCanvasEvent(projectId, saveEvent)
        .then(response => {
          if (response.data.updated) {
            setLastModified(response.data.lastModified);
          }
          //insert delay here
          setSaving(false);
        })
        .catch(error => {});
    };

    const checkDBConnections = id => {
      const ps = useProjectStore.getState();
      if (ps.targetDatabaseId === '') return;

      const nodesAndEdges = [...nodes, ...edges];
      let replacedNodesAndEdges = nodesAndEdges;

      let oldDbid = ps.dbIds.get(id.componentId);

      if (oldDbid != undefined) {
        ps.dbIds.delete(id.componentId);

        if (new Set(ps.dbIds.values()).has(oldDbid)) {
          if (id.displayName === 'Query Database') {
            replacedNodesAndEdges = nodesAndEdges.filter(node => {
              return !(node.target === id.componentId && node.source === oldDbid);
            });
          } else {
            replacedNodesAndEdges = nodesAndEdges.filter(node => {
              return !(node.source === id.componentId && node.target === oldDbid);
            });
          }
        } else {
          replacedNodesAndEdges = nodesAndEdges.filter(node => {
            return node.id != oldDbid;
          });
        }
      }

      let compData = nodesAndEdges.find(node => {
        return node.id === id.componentId;
      });
      if (ps.targetDatabaseId !== null) {
        ps.createDbNodeLogic(
          id,
          ps.dbIds,
          replacedNodesAndEdges,
          ps.targetDatabaseId,
          compData
        );
      }
      useCanvasStore.getState().setNodesAndEdges(replacedNodesAndEdges);
    };

    const removeForceStartTestParams = () => {
      const params = new URLSearchParams(window.location.search);
      params.delete('force_start');
      params.delete('selectedSession');
      navigate({ search: params.toString() });
    };

    const setWorkflowName = name => {
      let title = 'Untitled';
      if (name !== '' && name !== null && name !== undefined) title = name;
      document.title = `${title} | Workflow86`;
      if (workflow.name !== name) {
        // adding this to avoid re render
        setWorkflow({
          ...workflow,
          name: name,
        });
      }
    };

    const handleClosePaneButtonClick = () => {
      if (Date.now() - lastNodeClickTime.current < 200) {
        return;
      }
      setPendingConnectionSource(null);
      if (!isAnyForm(editComponent?.type)) {
        return;
      }
      handleClosePaneSave();
    };

    const closePane = () => {
      if (openTestPanel) {
        removeForceStartTestParams();
        setOpenTestPanel(false);
      }
    };

    // Handles the closing pane logic
    // Shared between the close pane button on the side of the pane
    // and the close button in the component pane header.
    // Uses direct save via keyed store — no saveTriggerValue needed, no timing dependency on mount state.
    const handleClosePaneSave = () => {
      useCanvasStore.getState().syncNodes();

      const comp = editComponent;
      if (comp && !production && !useProjectStore.getState().production) {
        saveComponentByType(comp.type, comp.componentId)
          .then(() => useProjectStore.setState({ savedComponent: true }))
          .catch(() => {});
      }

      const id = comp;
      setEditComponent(null);

      if (window.location.href.includes('?view=')) navigate(`/project/canvas/${projectId}`);

      setMode('DEFAULT');

      setPauseSave(false);

      setChildTemplateLoaded(false);

      checkDBConnections(id);
      closePane();
    };

    const openComponentAndTestPanel = componentId => {
      let openedComponent = nodes.find(comp => comp.id === componentId);
      if (!openedComponent) return;
      setOpenTestPanel(componentId);
    };

    const openInPane = compId => {
      let openedComponent = nodes.find(comp => comp.id === compId);
      if (!openedComponent) return;

      setEditComponent({
        componentId: compId,
        ...openedComponent.data,
      });
    };

    if (!loading)
      return (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}>
        <CanvasContext.Provider
          value={{
            isNoteClicked: [noteClicked, setNoteClicked],
            edit: [editComponent, setEditComponent],
            updated: [lastModified, setLastModified],
            isCanvasLocked: [isCanvasLocked, setIsCanvasLocked],
            reactFlowInstance: [reactFlowInstance, setReactFlowInstance],
            isCanvasLoaded: [isCanvasLoaded, setIsCanvasLoaded],
            testPanel: [openTestPanel, setOpenTestPanel],
            modeInfo: [mode, setMode],
            notesLoaded: [notesLoaded, setNotesLoaded],
            canvasInstance: [reactFlowInstance, setReactFlowInstance],
            savingInfo: [saving, setSaving],
            workflowVersion: production ? publishedVersion : draftVersion,
            productionVersion: publishedVersion,
            openPublishDialog: [
              showPublishDialogIfNoProductionVersion,
              setShowPublishDialogIfNoProductionVersion,
            ],
            loadDialog: [loadingDialog, setLoadingDialog],
            unpublishedChanges: [checkUnpublishedChanges],
            componentInfoHeader: [componentInfoForNodeHeader, setComponentInfoForNodeHeader],
            canvasViewport: [viewport, setViewport],
            prebuildPreview: [openPrebuildPreview, setOpenPrebuildPreview],
            mermaidJS: [mermaidJSContent, setMermaidJSContent],
            isChildTemplateLoaded: [childTemplateLoaded, setChildTemplateLoaded],
            workflowId: projectId,
            pendingConnectionSource: [pendingConnectionSource, setPendingConnectionSource],
            allPlaceholdersInWorkflow: allPlaceholdersInWorkflow,
            setAllPlaceholdersInWorkflow: setAllPlaceholdersInWorkflow,
            isOutputPlaceholdersLoading: isOutputPlaceholdersLoading,
            computedPlaceholdersInWorkflow,
            canonicalPlaceholdersInWorkflow,
            placeholderMapSource,
            placeholderParityResult,
            globalPlaceholders,
            credentialPlaceholders,
            refreshAllPlaceholdersInWorkflow,
            openComponentAndTestPanel,
            lastNodeClickTime,
            aiChat: [aiChatOpen, setAiChatOpen],
            collapseNodes,
          }}
        >
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
          {hasAiBuilderAccess && aiChatOpen && (
            <WorkflowAgentChat
              key={projectId}
              projectId={projectId}
              projectName={workflow?.name}
              onClose={() => setAiChatOpen(false)}
              reactFlowInstance={reactFlowInstance}
            />
          )}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            <CanvasHeader>
              <DraftCanvasHeader
                saving={saving}
                pauseSave={pauseSave}
                changes={changes}
                production={production}
                firePublish={() => {
                  fireValidateDialog('publish');
                }}
                setOpenTestDialog={() => {
                  setOpenTestPanelAfterValidation(openTestPanel);
                }}
                saveDraftVersion={saveDraftVersion}
                fireTestDialog={setOpenTestTriggerDialog}
                fireRenamePlaceholdersDialog={isOpen => setRenamePlaceholdersDialog(isOpen)}
                publishedVersion={publishedVersion}
                draftVersion={draftVersion}
                lastModified={lastModified}
                ProjectStore={useProjectStore}
                saveDraft={() => save()}
                openStartDialog={openStartDialog}
                closeStartDialog={() => {
                  setOpenStartDialog(false);
                }}
                setShowPublishDialogIfNoProductionVersion={
                  setShowPublishDialogIfNoProductionVersion
                }
                openInPane={openInPane}
                openAccess={openAccess}
                setOpenAccess={setOpenAccess}
                setOpenBrokenPHRename={bool => {
                  setAddressError(false);
                  setOpenBrokenPHRename(bool);
                  setRenamePlaceholdersDialog(bool);
                }}
                setRenameGivenPH={givenPH => {
                  setAddressError(false);
                  setRenameGivenPH(givenPH);
                  setRenamePlaceholdersDialog(true);
                }}
                setOpenTestPanel={setOpenTestPanel}
                navigate={navigate}
                setLoadingDialog={setLoadingDialog}
                checkUnpublishedChanges={checkUnpublishedChanges}
                isCanvasLocked={isCanvasLocked}
                workflow={workflow}
                setWorkflow={setWorkflow}
                setWorkflowName={setWorkflowName}
                handleSaveProjectAttributes={handleSaveProjectAttributes}
                handleBack={handleBack}
                projectId={projectId}
                hasAiBuilderAccess={hasAiBuilderAccess}
                aiChatOpen={aiChatOpen}
                onToggleAiChat={() => setAiChatOpen(prev => !prev)}
              />
            </CanvasHeader>

            <>
              <>
                {isAnyForm(editComponent?.type) && <ClosePaneButton
                  editComponent={editComponent}
                  handleClose={handleClosePaneButtonClick}
                  openTestPanel={openTestPanel}
                />}
                {isAnyForm(editComponent?.type) && <ComponentPane
                  isInit={isInit}
                  setIsInit={setIsInit}
                  showSuccess={showSuccess}
                  saving={saving}
                  setSaving={val => {
                    setSaving(val);
                  }}
                  openTestPanel={openTestPanel}
                  setOpenTestPanel={setOpenTestPanel}
                  setTestFromCompPanel={setTestFromCompPanel}
                  production={production}
                  open={openComponentPane}
                  pauseSave={pauseSave}
                  updatePauseSave={newPauseSave => {
                    if (newPauseSave) setMode('SAVE_PAUSED');
                    else setMode('DEFAULT');
                    setPauseSave(newPauseSave);
                  }}
                  version={production ? publishedVersion : draftVersion}
                  editComponent={editComponent}
                  close={handleClosePaneSave}
                  handleClosePaneButtonClick={handleClosePaneButtonClick}
                  projectId={projectId}
                  childTemplateLoaded={childTemplateLoaded}
                  setChildTemplateLoaded={val => {
                    setChildTemplateLoaded(val);
                  }}
                  mode={mode}
                  addressError={addressError}
                  setIsCanvasLocked={setIsCanvasLocked}
                />}
              </>
              {openTestPanel && (
                <TestPanel
                  isOpenWithComponent={
                    editComponent
                      ? config.PANE_WIDTH
                      : 0
                  }
                  executeTest={executeTest}
                  setExecuteTest={setExecuteTest}
                  showSuccess={showSuccess}
                  open={openTestPanel}
                  version={draftVersion}
                  projectId={projectId}
                  componentId={openTestPanel}
                  compData={nodes.find(comp => comp.id === openTestPanel)}
                  handleClose={handleCloseTestPanel}
                  handleExpand={handleTestExpand}
                  isExpanded={window.location.href.includes('fullScreen=true')}
                  save={async () => {
                    if (!production && editComponent && !editComponent.fromWorkflowPath) {
                      if (!useProjectStore.getState().production) {
                        await saveComponentByType(editComponent.type, editComponent.componentId);
                      }
                    }
                  }}
                  handleEdit={compId => {
                    let openedComponent = useProjectStore.getState().canvas?.find(comp => comp.id === compId);
                    if (!openedComponent) return;
                    setEditComponent({
                      componentId: compId,
                      ...openedComponent.data,
                    });
                    setTestFromCompPanel(false);
                    setOpenTestPanel(null);
                  }}
                  childTemplateLoaded={true}
                  fetchTestOutput={fetchTestOutput}
                  setFetchTestOutput={setFetchTestOutput}
                />
              )}
            </>

            {mode === 'DEFAULT' ? (
              <Sidebar
                draftVersion={draftVersion}
                setWorkflowName={setWorkflowName}
                handleSaveProjectAttributes={handleSaveProjectAttributes}
                getLatestVersion={getLatestVersion}
                refetchComponents={refetchComponents}
                editComponent={editComponent}
                nodes={nodes}
              />
            ) : null}
            <CanvasWrapper ref={reactFlowWrapper}>
              <Canvas
                  saving={saving}
                  setSaving={val => {
                    setSaving(val);
                  }}
                  setLinkWarning={val => {
                    setLinkWarning(val);
                  }}
                  setNavigationWarning={val => {
                    setNavigationWarning(val);
                  }}
                  workflowId={workflow.projectId}
                  isTemplate={false}
                  mode={mode}
                  reactFlowInstance={reactFlowInstance}
                  setReactFlowInstance={setReactFlowInstance}
                  reactFlowWrapper={reactFlowWrapper}
                  viewport={viewport}
                  showNotes={showNotes}
                  setShowNotes={setShowNotes}
                  componentInfoForNodeHeader={componentInfoForNodeHeader}
                  setShowErrorSnackbar={setShowErrorSnackbar}
                  handleClosePaneButtonClick={handleClosePaneButtonClick}
                  useBpmnJoinBehavior={workflow.useBpmnJoinBehavior}
                  collapseNodes={collapseNodes}
                  setCollapseNodes={setCollapseNodes}
                />
            </CanvasWrapper>
          </div>
          </div>

          {/* //remove this snackbar when all requests that trigger it are replaced
        with events */}
          {(showSnackbar || savedComponent) && (
            <Snackbar
              open={showSnackbar || savedComponent}
              onClose={handleCloseSnackbar}
              autoHideDuration={1000}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              sx={{ zIndex: 9999 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.06)',
                }}
              >
                <CircularProgress size={16} sx={{ color: '#6366f1', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary', lineHeight: 1.45 }}>
                  Saving changes...
                </Typography>
              </Box>
            </Snackbar>
          )}
          {isCanvasLocked && (
            <Snackbar
              open={isCanvasLocked}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              className="ai-editing-snackbar"
            >
              <Alert icon={false} className="ai-editing-notification">
                {isCanvasLocked === true ? 'AI is editing this workflow...' : isCanvasLocked}
              </Alert>
            </Snackbar>
          )}
          {validationLoading && (
            <div
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                height: '100vh',
                width: '100%',
                position: 'fixed',
              }}
            />
          )}
          <StyledSnackbar
            open={!!linkWarning}
            message="Close the component panel before opening another component"
            severity="error"
            onClose={handleCloseSnackbar}
            autoHideDuration={2000}
          />
          <StyledSnackbar
            open={!!showErrorSnackbar}
            message={showErrorSnackbar || ""}
            severity="error"
            onClose={() => setShowErrorSnackbar(null)}
          />
          <StyledSnackbar
            open={!!errorSaving}
            message="Your workflow could not be saved. If this error persists, please contact us"
            severity="error"
            onClose={handleCloseSnackbar}
          />
          {saving && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              open={saving}
              sx={{ zIndex: 9999 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.06)',
                }}
              >
                <CircularProgress size={16} sx={{ color: '#6366f1', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary', lineHeight: 1.45 }}>
                  Saving changes...
                </Typography>
              </Box>
            </Snackbar>
          )}
          <StyledSnackbar
            open={!!production}
            message="This is a view of the production workflow. It cannot be edited and changes will not be saved."
            severity="info"
            onClose={() => {}}
            autoHideDuration={null}
          />
          {/* DIALOGS */}
          {projectId && renamePlaceholdersDialog && (
            <RenamePlaceholdersDialog
              workflow={workflow}
              projectId={projectId}
              isOpen={renamePlaceholdersDialog}
              closeDialog={() => {
                setRenamePlaceholdersDialog(false);
              }}
              openBrokenPHRename={openBrokenPHRename}
              setOpenBrokenPHRename={setOpenBrokenPHRename}
              renameGivenPH={renameGivenPH}
              setRenameGivenPH={setRenameGivenPH}
              openInPane={openInPane}
              forceCloseComponentPanes={() => {
                if (editComponent) {
                  handleClosePaneSave();
                }
              }}
            />
          )}
          {loadingDialog && (
            <CustomDialog
              size={'sm'}
              isOpen={loadingDialog}
              contents={
                <Grid container item xs={12} direction={'column'} alignItems={'center'}>
                  {runLoading()}
                  {loadingDialog && <p>Switching workflow view</p>}
                  {/* {validationLoading && <p>Checking workflow for issues...</p>} */}
                </Grid>
              }
            />
          )}
          {openTestTriggerDialog && (
            <CustomDialog
              isOpen={openTestTriggerDialog}
              title={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <ScienceIcon /> Start a test run
                </div>
              }
              contents={
                <>
                  <p>
                    You can start a test from any component by clicking the component and then
                    clicking the test run button.
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/images/testFromTrigger.svg"
                      style={{ height: '300px', width: 'auto' }}
                    />
                  </div>
                </>
              }
              buttons={
                <>
                  <Button
                    onClick={() => {
                      setOpenTestTriggerDialog(false);
                    }}
                    variant={'outlined'}
                    color={'info'}
                    disabled={loading}
                  >
                    Close
                  </Button>
                </>
              }
            ></CustomDialog>
          )}
          {publishDialog && (
            <WorkflowPublishConfirmDialog
              open={publishDialog}
              draftVersion={draftVersion}
              workflow={workflow}
              contents={publishedVersion}
              lastPublished={lastPublished}
              publishComment={setPublishComment}
              onClickCancelButton={() => setPublishDialog(false)}
              isPublishing={loadPublish}
              onClickPublishButton={onPublish}
              projectId={projectId}
            />
          )}
          {publishConfirmDialog && (
            <WorkflowPublishedDialog
              projectId={projectId}
              publishConfirmDialog={publishConfirmDialog}
              lastPublished={lastPublished}
              publishedVersion={publishedVersion}
              draftVersion={draftVersion}
              setPublishConfirmDialog={setPublishConfirmDialog}
              setOpenStartDialog={setOpenStartDialog}
              onDonePublish={onDonePublish}
              components={projectComponents}
              setOpenAccess={setOpenAccess}
              publishComment={publishComment}
              navigate={navigate}
              setLoadingDialog={setLoadingDialog}
              unpublishedChanges={checkUnpublishedChanges}
              workflow={workflow}
            />
          )}
          {(addressError || validationLoading) && (
            <FixErrorsDialog
              open={addressError || validationLoading}
              fireValidateDialog={fireValidateDialog}
              validationLoading={validationLoading}
              errorsNumber={projectErrors.length}
              errorsArray={projectErrors}
              project_id={projectId}
              close={() => {
                setAddressError(false);
                if (testFromCompPanel) {
                  let openedComponent = projectCanvas?.find(comp => comp.id === openTestPanel);
                  if (!openedComponent) return;

                  setEditComponent({
                    componentId: openTestPanel,
                    ...openedComponent.data,
                  });
                  setTestFromCompPanel(false);
                }
                setOpenTestPanel(null);
              }}
              handlePublish={() => {
                setAddressError(false);
                setPublishDialog(true);
              }}
              openInPane={openInPane}
              validatingType={validatingType}
              setOpenTestDialog={() => {
                setOpenTestPanelAfterValidation(null);
                setAddressError(false);
                setOpenTestPanelAfterValidation(openTestPanel);
              }}
              setOpenBrokenPHRename={bool => {
                setOpenBrokenPHRename(bool);
                setRenamePlaceholdersDialog(bool);
              }}
              setRenameGivenPH={givenPH => {
                setRenameGivenPH(givenPH);
                setRenamePlaceholdersDialog(true);
              }}
              setNavigationWarning={setNavigationWarning}
              editComponent={editComponent}
            />
          )}
          {showErrorWorkflowDialog && (
            <ErrorWorkflowDialog
              open={showErrorWorkflowDialog}
              setOpen={setShowErrorWorkflowDialog}
              projectId={projectId}
              saveAsNewVersion={() => {
                saveDraftVersion();
                setShowErrorWorkflowDialog(false);
              }}
            />
          )}
          {/* Restore prompt for deleted workflows accessed via trash view */}
          {showRestorePrompt && (
            <CustomDialog
              isOpen={showRestorePrompt}
              title={<div>Workflow is in Trash</div>}
              contents={
                <>
                  <p>This workflow has been deleted and is in the Trash.</p>
                  <p>You need to restore it before you can view or edit it.</p>
                </>
              }
              buttons={
                <>
                  <ActionButton
                    variant="contained"
                    bgColor="#FFFFFF"
                    textColor="#212121"
                    borderColor="#212121"
                    onClick={() => {
                      navigate('/project/trash');
                    }}
                  >
                    Go to Trash
                  </ActionButton>
                  <Button
                    variant="contained"
                    color={'primary'}
                    onClick={async () => {
                      try {
                        await send_request(
                          'project-service/project/restore',
                          {},
                          { projectId },
                          'post'
                        );
                        // Reload the page to load the restored workflow
                        window.location.reload();
                      } catch (error) {
                        console.error('Error restoring workflow:', error);
                        setShowSnackbar(true);
                      }
                    }}
                  >
                    Restore & View
                  </Button>
                </>
              }
            />
          )}
          {showPublishDialogIfNoProductionVersion && (
            <CustomDialog
              isOpen={showPublishDialogIfNoProductionVersion}
              title={<div>This workflow has not been published</div>}
              contents={
                <>
                  <p>You will need to publish this workflow into production before running it.</p>
                </>
              }
              buttons={
                <>
                  <ActionButton
                    variant="contained"
                    bgColor="#FFFFFF"
                    textColor="#212121"
                    borderColor="#212121"
                    onClick={() => {
                      setShowPublishDialogIfNoProductionVersion(false);
                    }}
                  >
                    Go Back
                  </ActionButton>
                  <Button
                    variant="contained"
                    color={'error'}
                    onClick={() => {
                      fireValidateDialog('publish');
                    }}
                  >
                    Publish Now
                  </Button>
                </>
              }
            ></CustomDialog>
          )}
          {showComponentNotFoundDialog && (
            <CustomDialog
              isOpen={showComponentNotFoundDialog}
              title="Component Not Found"
              contents={<p>{`Component ${componentId} does not exist in the latest version!`}</p>}
              buttons={
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowComponentNotFoundDialog(false);
                  }}
                >
                  Close
                </Button>
              }
            />
          )}
          {openPrebuildPreview && (
            <PreviewDiagramPopup
              mermaidJSContent={mermaidJSContent}
              setOpenPrebuildPreview={setOpenPrebuildPreview}
            />
          )}
        </CanvasContext.Provider>
        </div>
      );
    else return showCanvasLoadingSkeleton();
};

export default WorkflowCanvas;
